import { StateCreator } from "zustand";
import type { User } from "./auth.types";

/**
 * Auth slice state.
 */
export type AuthSliceState = {
  user: User | null;
  authToken: string | null;
  isTokenExpired: boolean;
};

/**
 * Auth slice actions.
 */
export type AuthSliceActions = {
  setUser: (user: User | null, authToken?: string | null) => void;
  setTokenExpired: (expired: boolean) => void;
  clearAuth: () => void;
};

/**
 * Full auth slice type.
 */
export type AuthSlice = AuthSliceState & AuthSliceActions;

/**
 * Initial state for auth slice.
 */
export const initialAuthState: AuthSliceState = {
  user: null,
  authToken: null,
  isTokenExpired: false,
};

/**
 * Creates the auth slice for Zustand store.
 * Handles user authentication state.
 *
 * @example
 * ```typescript
 * const useStore = create<AuthSlice>()((...a) => ({
 *   ...createAuthSlice(...a),
 * }));
 * ```
 */
export const createAuthSlice: StateCreator<AuthSlice, [], [], AuthSlice> = (
  set
) => ({
  ...initialAuthState,

  setUser: (user, authToken) =>
    set((state) => ({
      user,
      authToken: authToken ?? state.authToken,
    })),

  setTokenExpired: (isTokenExpired) => set({ isTokenExpired }),

  clearAuth: () =>
    set({
      user: null,
      authToken: null,
      isTokenExpired: false,
    }),
});

// --- Selectors ---
export const auth = {
  state: (s: AuthSlice) => ({
    user: s.user,
    token: s.authToken,
    isTokenExpired: s.isTokenExpired,
    setUser: s.setUser,
    setTokenExpired: s.setTokenExpired,
    clearAuth: s.clearAuth,
  }),
};
